import React, { Component } from "react";
import type { Location, NavigateFunction } from "react-router-dom";
import { getAuthRequestUrl, validateAuthToken } from "../../api";
import { formatAuthError } from "../../auth/formatAuthError";
import { getOAuthRedirectUriForLogin } from "../../auth/oauthRedirect";
import { getCompanionSession, saveCompanionSession, setUserProfile } from "../../auth/session";
import { getDashboardLoginPath, getDashboardOverviewPath } from "../../utils/dashboardPaths";
import "./LoginPage.scss";

let oauthCodeInflight: string | null = null;

interface LoginPageProps {
  navigate: NavigateFunction;
  location: Location;
}

interface LoginPageState {
  isStartingAuth: boolean;
  isProcessingCallback: boolean;
  errorMessage: string | null;
}

class LoginPage extends Component<LoginPageProps, LoginPageState> {
  constructor(props: LoginPageProps) {
    super(props);
    this.state = {
      isStartingAuth: false,
      isProcessingCallback: false,
      errorMessage: null,
    };
  }

  componentDidMount(): void {
    const session = getCompanionSession();
    if (session) {
      this.props.navigate(getDashboardOverviewPath(), { replace: true });
      return;
    }
    void this.handleOAuthRedirect();
  }

  private startGoogleAuth(): Promise<void> {
    if (this.state.isStartingAuth) {
      return Promise.resolve();
    }

    this.setState({ isStartingAuth: true, errorMessage: null });
    return getAuthRequestUrl({
      scope: "profile",
      redirectUri: getOAuthRedirectUriForLogin(),
    })
      .then((resp) => {
        if (resp.status === "FAIL") {
          throw new Error(resp.error ?? "Could not start sign-in.");
        }
        if (!resp.request_url) {
          throw new Error("Missing request_url from auth_request_url response");
        }
        window.location.href = resp.request_url;
      })
      .catch((err: unknown) => {
        this.setState({
          isStartingAuth: false,
          errorMessage: formatAuthError(err),
        });
      });
  }

  private async handleOAuthRedirect(): Promise<void> {
    const { location, navigate } = this.props;
    const params = new URLSearchParams(location.search);
    const oauthError = params.get("error");
    const code = params.get("code");

    if (oauthError) {
      const desc = params.get("error_description") ?? oauthError;
      this.setState({ errorMessage: desc });
      navigate(getDashboardLoginPath(), { replace: true });
      return;
    }

    if (!code) {
      return;
    }

    if (oauthCodeInflight === code) {
      return;
    }
    oauthCodeInflight = code;

    this.setState({ isProcessingCallback: true, errorMessage: null });

    try {
      const resp = await validateAuthToken({
        token: code,
        scope: "profile",
        redirect_uri: getOAuthRedirectUriForLogin(),
      });
      if (resp.status !== "SUCCESS" || !resp.sid) {
        const msg = resp.error ?? "Authentication failed.";
        this.setState({ errorMessage: msg, isProcessingCallback: false });
        navigate(getDashboardLoginPath(), { replace: true });
        oauthCodeInflight = null;
        return;
      }
      const email = resp.email_id;
      if (!email) {
        this.setState({
          errorMessage: "Missing email in auth response.",
          isProcessingCallback: false,
        });
        navigate(getDashboardLoginPath(), { replace: true });
        oauthCodeInflight = null;
        return;
      }
      saveCompanionSession({ sid: resp.sid, email });
      setUserProfile({
        agent_email: resp.agent_email,
        agent_name: resp.agent_name,
        name: resp.user_name,
        pfp: resp.pfp,
        email_id: resp.email_id,
      });
      oauthCodeInflight = null;
      navigate(getDashboardOverviewPath(), { replace: true });
    } catch (err: unknown) {
      this.setState({
        errorMessage: formatAuthError(err),
        isProcessingCallback: false,
      });
      navigate(getDashboardLoginPath(), { replace: true });
      oauthCodeInflight = null;
    }
  }

  render(): React.JSX.Element {
    const { isStartingAuth, isProcessingCallback, errorMessage } = this.state;
    const busy = isStartingAuth || isProcessingCallback;

    return (
      <div className="login-page">
        <div className="login-page-card">
          <h1 className="login-page-title">Start Free</h1>
          <p className="login-page-subtitle">first 30 email threads on us</p>

          <button
            type="button"
            className="login-page-google"
            onClick={() => {
              void this.startGoogleAuth();
            }}
            disabled={busy}
          >
            {busy ? "Please wait…" : "Login with Google"}
          </button>

          <p className="login-page-footnote">no permission required.</p>

          {errorMessage ? (
            <p className="login-page-error" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
}

export default LoginPage;
